import { useCallback, useEffect, useRef, useState } from "react";
export const useInfiniteScroll = ({
  loadMore,
  hasMore,
  isLoading,
  rootMargin = "100px",
  threshold = 0.1,
  enabled = true,
  maxRetries = 3
}) => {
  const sentinelRef = useRef(null);
  const observerRef = useRef(null);
  const loadingRef = useRef(false);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const handleLoadMore = useCallback(async () => {
    if (loadingRef.current || isLoading || !hasMore || error) return;
    loadingRef.current = true;
    setError(null);
    try {
      await loadMore();
      setRetryCount(0);
    } catch (err) {
      console.error("Failed to load more items:", err);
      setError("Nie udało się załadować więcej elementów");
      setRetryCount(prev => prev + 1);
    } finally {
      loadingRef.current = false;
      setIsRetrying(false);
    }
  }, [loadMore, isLoading, hasMore, error]);
  const retry = useCallback(async () => {
    if (retryCount >= maxRetries) return;
    setIsRetrying(true);
    await handleLoadMore();
  }, [handleLoadMore, retryCount, maxRetries]);
  const clearError = useCallback(() => {
    setError(null);
    setRetryCount(0);
  }, []);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !enabled || !hasMore || error) return;

    // Cleanup previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting && !isLoading && hasMore && !loadingRef.current) {
        handleLoadMore();
      }
    }, {
      root: null,
      rootMargin,
      threshold
    });
    observer.observe(sentinel);
    observerRef.current = observer;
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [enabled, hasMore, isLoading, handleLoadMore, error, rootMargin, threshold]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);
  return {
    sentinelRef,
    error,
    retryCount,
    isRetrying,
    retry,
    clearError
  };
};